package com.advaita.cloud.drive.ui.api

import com.advaita.cloud.drive.shared.FileDto
import com.advaita.cloud.drive.shared.FileIdentity
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.coroutineScope
import kotlinx.coroutines.delay
import org.w3c.files.Blob
import org.w3c.files.File
import org.w3c.xhr.XMLHttpRequest

/**
 * Browser-side orchestrator for presigned uploads. One instance per `upload()`
 * call. Picks single-PUT or multipart based on file size; for multipart, runs
 * up to [initialPartBudget] PUTs in parallel and reports rolling-window
 * throughput through the [Progress] callback.
 *
 * The default budget (32) follows CoreWeave's "Performance best practices"
 * guidance — they recommend 300 concurrent ops per cluster node. Browser
 * tabs aren't cluster nodes so we cap lower, but 32 is well past the
 * single-stream throttle point CoreWeave applies to writes.
 */
class UploadOrchestrator(
    /** Initial part-PUT budget for THIS file's multipart upload. The store can
     *  grow it later via [growBudget] as sibling files complete and free up
     *  global capacity. */
    initialPartBudget: Int = 32,
    /** Retry budget for backend calls (start/getPartUrl/confirm/complete). Tiny
     *  backend calls — riding out a brief az2 restart or token refresh is cheap,
     *  and a single failure cancels the file. With exp backoff 500/1000/2000/
     *  4000/8000 ms = ~15s total wait at 6. */
    private val backendRetries: Int = 6,
    /** Optional durable session store. When non-null, [uploadMultipart] persists a
     *  [PlainRecord] on start and deletes it on completion so an interrupted upload
     *  can be resumed later via [resume]. When null, all persistence no-ops. */
    private val store: ResumableUploadStore? = null,
) {

    private var currentBudget: Int = initialPartBudget

    /**
     * How long we chase a "warm" socket via the 5s fail-fast timeout. After this
     * window we accept whatever sockets we got and let parts run to completion
     * at their natural speed — better to upload slow than to keep retrying
     * forever in a low-throughput network. Set per the user's request: "if we
     * don't arrive on a good one within a minute just give up and take what we get."
     */
    private val warmupWindowMs: Long = 60_000L

    /** Set at the start of upload(); used by putWithRetries to switch the xhr
     *  timeout from "tight, chase warm socket" to "long, accept what we get". */
    private var uploadStartMs: Long = 0L

    /**
     * Token bucket for in-flight part PUTs. We can't use kotlinx Semaphore because
     * it tracks acquires/releases as a balance and throws on release-past-capacity —
     * which makes it impossible to grow when siblings finish. A Channel<Unit> with
     * UNLIMITED buffer acts as a resizable semaphore: receive=acquire, trySend=release,
     * extra trySends = grow.
     */
    private val partTokens: Channel<Unit> = Channel<Unit>(Channel.UNLIMITED).also { ch ->
        repeat(initialPartBudget) { ch.trySend(Unit) }
    }

    private suspend fun acquirePartSlot() { partTokens.receive() }
    private fun releasePartSlot() { partTokens.trySend(Unit) }

    /**
     * Grow the part-PUT budget for this orchestrator by [delta] permits. Called
     * by the store when a sibling upload finishes and there's capacity to share.
     * Permits only grow — never shrink — so this is safe to call from outside
     * the orchestrator's coroutine.
     */
    fun growBudget(delta: Int) {
        if (delta <= 0) return
        repeat(delta) { partTokens.trySend(Unit) }
        currentBudget += delta
    }

    /** Current allocated budget (informational; used by the store's rebalancer). */
    fun currentPartBudget(): Int = currentBudget

    private val throughputWindowMs = 5_000L

    data class Progress(
        val partsDone: Int,
        val partsInFlight: Int,
        val partsTotal: Int,
        val bytesDone: Long,
        val bytesTotal: Long,
        val bytesPerSecond: Double?,
        val etaSeconds: Long?,
        /** True while we're still racing to find a warm socket (multipart only).
         *  UI shows "initializing…" while this is true; flips to false once a
         *  part confirms OR the warmup window elapses, whichever comes first. */
        val inWarmup: Boolean = false,
    )

    /** ADV-1330: [target] is My Drive's folder or a shared folder ([CreateTarget]). */
    suspend fun upload(
        target: CreateTarget,
        file: File,
        relativePath: String? = null,
        onProgress: (Progress) -> Unit = {},
    ): FileDto {
        uploadStartMs = nowMillis()
        val sizeBytes = (file.size.toString().toLongOrNull() ?: 0L)
        // Single unified path: every file goes through multipart regardless of
        // size (a 1-byte file is a 1-part upload). The backend rejects 0-byte
        // files — S3/CoreWeave multipart cannot have an empty part.
        return uploadMultipart(target, file, sizeBytes, relativePath, onProgress)
    }

    /** Min ms between progress emissions per stream. xhr.upload.onprogress can
     *  fire 30+ Hz; with N concurrent streams each triggering Compose state
     *  updates, the JS main thread saturates re-rendering and starves the OS
     *  callbacks that feed sockets more bytes. Throttling to ~4 Hz per stream
     *  keeps the bar smooth while leaving the main thread free for I/O. */
    private val progressEmitIntervalMs: Long = 250L

    private suspend fun uploadMultipart(
        target: CreateTarget,
        file: File,
        sizeBytes: Long,
        relativePath: String?,
        onProgress: (Progress) -> Unit,
    ): FileDto = coroutineScope {
        val started: com.advaita.cloud.drive.shared.UploadStartMultipartResponse = withBackendRetries("startMultipart") {
            FilesApi.startMultipartUpload(
                uploadStartBody(target, file.name, file.type.takeIf { it.isNotBlank() }, sizeBytes, relativePath)
            )
        }
        // ADV-1330 (Question 4 (a)): a shared destination is stored by id, so a resume after a
        // reload goes back to the same shared folder.
        val parent = target.recordParent()
        // Cross-tab liveness: hold the session's Web Lock for the upload's
        // lifetime so other tabs' scans don't offer this in-flight session as
        // "interrupted". Held BEFORE the record is persisted so no scan can
        // observe a lockless-but-live record. Lock trouble must never break an
        // upload, hence runCatching.
        val liveLock = runCatching { UploadSessionLocks.hold(started.sessionId) }.getOrNull()
        try {
            runCatching {
                store?.put(
                    PlainRecord(
                        sessionId = started.sessionId,
                        identity = FileIdentity(file.name, sizeBytes, (file.asDynamic().lastModified as Double).toLong()),
                        parentPath = parent.path,
                        relativePath = relativePath,
                        mimeType = file.type.takeIf { it.isNotBlank() },
                        partSize = started.partSize,
                        totalParts = started.totalParts,
                        createdAt = nowMillis().toDouble(),
                        parentId = parent.id,
                        parentName = parent.name,
                    ),
                )
            }.onFailure { console.log("[drive] resume-record put failed: ${it.message}") }
            val result = runSession(
                file, sizeBytes, started.sessionId,
                started.partSize, started.totalParts, (1..started.totalParts).toList(),
                relativePath, onProgress,
            )
            runCatching { store?.delete(started.sessionId) }
                .onFailure { console.log("[drive] resume-record delete failed: ${it.message}") }
            result
        } finally {
            liveLock?.release()
        }
    }

    /**
     * Resume an interrupted multipart upload after the user re-selects the matching
     * file. The server ([FilesApi.getUploadStatus]) is authoritative for which parts
     * still need uploading; we run only [UploadStatusResponse.partsRemaining]. The session
     * already names its destination, so a resume takes none.
     */
    suspend fun resume(
        file: File,
        record: PlainRecord,
        onProgress: (Progress) -> Unit = {},
    ): FileDto = coroutineScope {
        uploadStartMs = nowMillis()
        val sizeBytes = record.identity.size
        // Same cross-tab liveness lock as a fresh upload — a resumed session is
        // live again and must not be offered in other tabs while it runs.
        val liveLock = runCatching { UploadSessionLocks.hold(record.sessionId) }.getOrNull()
        try {
            val status = withBackendRetries("getUploadStatus") { FilesApi.getUploadStatus(record.sessionId) }
            val result = runSession(
                file, sizeBytes, record.sessionId,
                record.partSize, record.totalParts, status.partsRemaining,
                record.relativePath, onProgress,
            )
            runCatching { store?.delete(record.sessionId) }
                .onFailure { console.log("[drive] resume-record delete failed: ${it.message}") }
            result
        } finally {
            liveLock?.release()
        }
    }

    /**
     * Shared per-part runner + complete. Uploads exactly the parts in [partsToRun]
     * (all parts for a fresh upload; only the server's remaining parts on resume),
     * then completes the session. All warmup/retry/throughput/progress semantics are
     * identical for both entry points — only the set of parts differs.
     */
    private suspend fun runSession(
        file: File,
        sizeBytes: Long,
        sessionId: String,
        partSize: Long,
        totalParts: Int,
        partsToRun: List<Int>,
        relativePath: String?,
        onProgress: (Progress) -> Unit,
    ): FileDto = coroutineScope {
        val window = ThroughputWindow()
        val perPartBytes = LongArray(totalParts + 1)
        val toRun = partsToRun.toSet()
        // Pre-fill already-done parts so the progress bar starts at the resumed
        // fraction rather than 0. (perPartBytes is 1-based, size totalParts+1.)
        for (n in 1..totalParts) {
            if (n !in toRun) perPartBytes[n] = minOf(partSize, sizeBytes - (n - 1).toLong() * partSize)
        }
        var doneCount = totalParts - partsToRun.size
        var inFlightCount = 0
        val startedMs = uploadStartMs.also { check(it > 0L) { "uploadStartMs not set" } }
        window.record(startedMs, totalSent(perPartBytes))
        fun isInWarmup(): Boolean = doneCount == 0 && (nowMillis() - startedMs) < warmupWindowMs
        emit(onProgress, doneCount, 0, totalParts, totalSent(perPartBytes), sizeBytes, window, isInWarmup())

        val sessionTag = sessionId.take(8)
        fun t(): String {
            val ms = nowMillis() - startedMs
            return "T+${(ms / 100).toInt() / 10.0}s"  // 0.1s precision, e.g. T+12.3s
        }
        console.log("[drive] $sessionTag UPLOAD start: ${partsToRun.size}/$totalParts parts × $partSize bytes (budget=$currentBudget)")
        // Per-part throttle so concurrent streams don't all fire onprogress -> Compose
        // recomposition at network-buffer speed. Each stream tracks its own last-emit
        // timestamp; the part-confirmed emit always fires.
        val lastEmitMs = LongArray(totalParts + 1)
        suspend fun runPart(partNumber: Int) {
            val offset = (partNumber - 1).toLong() * partSize
            val end = minOf(offset + partSize, sizeBytes)
            val blob = sliceBlob(file, offset, end)
            val tGetUrlStart = nowMillis()
            console.log("[drive] $sessionTag ${t()} part $partNumber: getPartUrl…")
            val signed = withBackendRetries("getPartUrl-$partNumber") {
                FilesApi.getPartUrl(sessionId, partNumber)
            }
            val tPutStart = nowMillis()
            val getUrlMs = tPutStart - tGetUrlStart
            console.log("[drive] $sessionTag ${t()} part $partNumber: PUT start (${end - offset} B) [getPartUrl ${getUrlMs}ms]")
            inFlightCount += 1
            try {
                val (etag, _) = putWithRetries(blob, signed.url, signed.method, signed.headers, partNumber) { sent ->
                    // Monotonic per-part bytes: a retried PUT restarts its
                    // xhr.upload.onprogress counter at 0, so we'd flicker
                    // backward if we wrote `sent` verbatim. Keep the max we've
                    // observed across attempts; this also means the bar
                    // doesn't "reset" if a slow socket gets killed at 4s.
                    if (sent > perPartBytes[partNumber]) {
                        perPartBytes[partNumber] = sent
                    }
                    val now = nowMillis()
                    if (now - lastEmitMs[partNumber] >= progressEmitIntervalMs) {
                        lastEmitMs[partNumber] = now
                        val total = totalSent(perPartBytes)
                        window.record(now, total)
                        emit(onProgress, doneCount, inFlightCount, totalParts, total, sizeBytes, window, isInWarmup())
                    }
                }
                val finalEtag = etag
                    ?: throw RuntimeException("part $partNumber returned no ETag — bucket CORS may not expose it")
                val tConfirmStart = nowMillis()
                val putMs = tConfirmStart - tPutStart
                val putKBps = if (putMs > 0) ((end - offset) * 1000L / putMs / 1024L) else 0L
                console.log("[drive] $sessionTag ${t()} part $partNumber: PUT done [PUT ${putMs}ms ≈ ${putKBps} KiB/s]; confirmPart…")
                withBackendRetries("confirmPart-$partNumber") {
                    FilesApi.confirmPart(sessionId, partNumber, finalEtag, end - offset)
                }
                perPartBytes[partNumber] = end - offset
                doneCount += 1
                val tDone = nowMillis()
                val confirmMs = tDone - tConfirmStart
                val totalMs = tDone - tGetUrlStart
                console.log("[drive] $sessionTag ${t()} part $partNumber: confirmed [confirm ${confirmMs}ms, total ${totalMs}ms]")
            } finally {
                inFlightCount -= 1
            }
            val total = totalSent(perPartBytes)
            window.record(nowMillis(), total)
            emit(onProgress, doneCount, inFlightCount, totalParts, total, sizeBytes, window, isInWarmup())
        }

        try {
            // No prewarm — Chrome partitions its connection pool by CORS mode,
            // so no-cors GETs to the bucket origin end up in a different pool
            // bucket than the cross-origin PUTs and don't actually warm them.
            // Instead, rely on PUT timeouts + retries: when Chrome opens N
            // sockets in parallel, one wins the slow-start race; the others
            // stall. The xhr.timeout in putBlob kills stalled PUTs at 90s, and
            // putWithRetries reissues them onto the keep-alive pool — which
            // is dominated by the winning socket.
            val jobs = partsToRun.map { n ->
                async {
                    acquirePartSlot()
                    try { runPart(n) } finally { releasePartSlot() }
                }
            }
            jobs.awaitAll()
        } catch (t: Throwable) {
            runCatching { FilesApi.abortUpload(sessionId) }
            throw t
        }

        val tCompleteStart = nowMillis()
        console.log("[drive] $sessionTag ${t()} all parts confirmed; complete…")
        val result = withBackendRetries("complete") { FilesApi.completeUpload(sessionId) }
        val tEnd = nowMillis()
        val totalMs = tEnd - startedMs
        val completeMs = tEnd - tCompleteStart
        val mibPerSec = if (totalMs > 0) (sizeBytes.toDouble() / 1_048_576.0) / (totalMs.toDouble() / 1000.0) else 0.0
        val mibPerSecRounded = (mibPerSec * 10).toInt() / 10.0
        console.log("[drive] $sessionTag ${t()} UPLOAD done [complete ${completeMs}ms, total ${totalMs}ms ≈ $mibPerSecRounded MiB/s on ${sizeBytes} bytes]")
        result
    }

    /**
     * Retry a backend call with exponential backoff. Designed to absorb transient
     * 401s (token blip while az2 is restarting or refreshing), 5xx errors, and
     * network blips during a long upload. Uses a longer retry budget than
     * putWithRetries because backend calls are tiny — multiple seconds of retry
     * is cheap, and giving up cancels the whole file.
     */
    internal suspend fun <T> withBackendRetries(label: String, block: suspend () -> T): T {
        var lastErr: Throwable? = null
        for (attempt in 1..backendRetries) {
            try {
                return block()
            } catch (t: Throwable) {
                // ADV-1326c (ruling 6): a refusal is an answer, not a blip. The store polls the
                // standing once on it, so it must arrive at once and unwrapped. ADV-1330: the
                // same for any 403 or 404 (a shared folder's grant lowered or removed).
                if (t.isCapabilityDenied() || t.isRefusal()) throw t
                lastErr = t
                if (attempt < backendRetries) {
                    val backoffMs = (500L * (1L shl (attempt - 1))).coerceAtMost(8000L)
                    delay(backoffMs)
                }
            }
        }
        throw RuntimeException("$label failed after $backendRetries attempts", lastErr)
    }

    private suspend fun putWithRetries(
        blob: Blob,
        url: String,
        method: String,
        headers: Map<String, String>,
        partNumber: Int,
        onProgressBytes: (Long) -> Unit,
    ): Pair<String?, Int> {
        var lastErr: Throwable? = null

        // PHASE 1 — Warmup: 5s timeout per attempt, retry unbounded until
        // the warmup window expires. Without an unbounded retry budget here,
        // a uniformly-stuck part can chew through its retry quota in N×5s
        // and never reach the post-warmup fallback. Observed in the wild:
        // part 5 hit 8 retries × 5s = 40s while another socket was warm and
        // serving 50 MiB/s, but Chrome's pool kept handing part 5's retries
        // to other cold sockets. The fix is to keep firing 5s attempts for
        // the full 60s window, then drop the timeout entirely.
        var warmupAttempt = 0
        while ((nowMillis() - uploadStartMs) < warmupWindowMs) {
            warmupAttempt++
            try {
                return putBlob(blob, url, method, headers, 5_000, onProgressBytes)
            } catch (t: Throwable) {
                lastErr = t
                val backoffMs = (50L * (1L shl ((warmupAttempt - 1).coerceAtMost(5)))).coerceAtMost(1000L)
                delay(backoffMs)
            }
        }

        // PHASE 2 — Post-warmup: no timeout, let it run to completion at
        // whatever speed the socket actually delivers. A few attempts here
        // because even a no-timeout PUT can fail for real reasons (network
        // reset, expired signed URL, 5xx) — but most uploads succeed on
        // attempt 1.
        val postWarmupRetries = 3
        for (attempt in 1..postWarmupRetries) {
            try {
                return putBlob(blob, url, method, headers, 0, onProgressBytes)
            } catch (t: Throwable) {
                lastErr = t
                if (attempt < postWarmupRetries) delay(500L * attempt)
            }
        }
        throw RuntimeException(
            "part $partNumber failed (warmup attempts=$warmupAttempt + $postWarmupRetries post-warmup)",
            lastErr,
        )
    }

    private suspend fun putBlob(
        blob: Blob,
        url: String,
        method: String,
        headers: Map<String, String>,
        /** XHR timeout in ms. 0 = no timeout (let it run to completion). */
        timeoutMs: Int,
        onProgressBytes: (Long) -> Unit,
    ): Pair<String?, Int> {
        val done = CompletableDeferred<Pair<String?, Int>>()
        val xhr = XMLHttpRequest()
        xhr.open(method, url, async = true)
        headers.forEach { (k, v) -> xhr.setRequestHeader(k, v) }
        // 5s during warmup chases warm sockets via abort-and-retry; 0 (no
        // timeout) post-warmup or for single-PUT lets a slow upload finish at
        // its natural pace. Caller decides which to pass.
        xhr.timeout = timeoutMs
        xhr.upload.onprogress = { event ->
            val loaded = event.asDynamic().loaded as? Number
            if (loaded != null) onProgressBytes(loaded.toLong())
        }
        xhr.onload = {
            val status = xhr.status.toInt()
            if (status in 200..299) done.complete(xhr.getResponseHeader("ETag") to status)
            else done.completeExceptionally(
                    // ADV-443: typed, so the toast renders mapped copy instead of a raw status
                    // plus up to 200 chars of S3 XML. Body still logged by the caller.
                    ApiException(status, "upload part", xhr.responseText.take(200)),
                )
        }
        xhr.onerror = { done.completeExceptionally(RuntimeException("PUT network error — check bucket CORS")) }
        xhr.ontimeout = { done.completeExceptionally(RuntimeException("PUT timed out at ${timeoutMs}ms — stalled socket; will retry")) }
        xhr.send(blob)
        return done.await()
    }

    private fun emit(
        onProgress: (Progress) -> Unit,
        partsDone: Int, partsInFlight: Int, partsTotal: Int,
        bytesDone: Long, bytesTotal: Long,
        window: ThroughputWindow,
        inWarmup: Boolean = false,
    ) {
        val bps = window.bytesPerSecond()
        val remaining = bytesTotal - bytesDone
        val eta = if (bps != null && bps > 0 && remaining > 0L) (remaining / bps).toLong() else null
        onProgress(Progress(partsDone, partsInFlight, partsTotal, bytesDone, bytesTotal, bps, eta, inWarmup))
    }

    private fun totalSent(perPart: LongArray): Long {
        var s = 0L; for (i in 1 until perPart.size) s += perPart[i]; return s
    }

    private fun sliceBlob(file: File, start: Long, end: Long): Blob =
        file.asDynamic().slice(start.toDouble(), end.toDouble()) as Blob

    private fun nowMillis(): Long = (js("Date.now()") as Number).toLong()

    /** Rolling-window throughput tracker — copied from the spike's SpikeMultipartUploader. */
    private inner class ThroughputWindow {
        private val samples = ArrayDeque<Pair<Long, Long>>()
        fun record(timestampMs: Long, bytesDone: Long) {
            samples.addLast(timestampMs to bytesDone)
            while (samples.size > 2 && samples.first().first < timestampMs - throughputWindowMs) {
                samples.removeFirst()
            }
        }
        fun bytesPerSecond(): Double? {
            if (samples.size < 2) return null
            val (t0, b0) = samples.first()
            val (t1, b1) = samples.last()
            val dt = t1 - t0
            if (dt <= 0L) return null
            val db = (b1 - b0).toDouble()
            if (db <= 0.0) return null
            return db / (dt.toDouble() / 1000.0)
        }
    }
}
